import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { useLocation } from 'react-router-dom';
import { resolvePageInfo } from '../../utils/routePageMeta';
import { resolveNavTopicIconForPath } from '../../utils/resolveNavTopicIcon';

export type WorkspacePageHeaderProps = {
  /** Override route-derived title. */
  title?: string;
  /** Override route-derived description. */
  description?: string;
  /** Optional actions aligned to the right of the title row. */
  actions?: React.ReactNode;
};

/**
 * Consistent post-login page chrome: icon, title, one short description.
 * Use at the top of workspace pages instead of ad-hoc h4/h5 blocks.
 */
const WorkspacePageHeader: React.FC<WorkspacePageHeaderProps> = ({
  title,
  description,
  actions,
}) => {
  const location = useLocation();
  const pageInfo = resolvePageInfo(location.pathname || '/');
  const icon = resolveNavTopicIconForPath(location.pathname || '/', location.search || '');
  const resolvedTitle = title || pageInfo.title;
  const resolvedDescription = description || pageInfo.pageDescription;

  return (
    <Box
      component="header"
      aria-label={resolvedTitle}
      sx={{
        mb: 2.5,
        pb: 2,
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.5}
        alignItems={{ xs: 'stretch', sm: 'flex-start' }}
        justifyContent="space-between"
      >
        <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ minWidth: 0, flex: 1 }}>
          <Box
            aria-hidden
            sx={{
              mt: 0.25,
              color: 'primary.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 40,
              height: 40,
              borderRadius: 1.5,
              bgcolor: 'action.hover',
              flexShrink: 0,
            }}
          >
            {icon}
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              component="h1"
              variant="h5"
              sx={{ fontWeight: 700, letterSpacing: 0.1, lineHeight: 1.25, mb: 0.5 }}
            >
              {resolvedTitle}
            </Typography>
            {resolvedDescription ? (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ lineHeight: 1.5, maxWidth: { md: '72ch' } }}
              >
                {resolvedDescription}
              </Typography>
            ) : null}
          </Box>
        </Stack>
        {actions ? (
          <Box sx={{ flexShrink: 0, display: 'flex', gap: 1, flexWrap: 'wrap' }}>{actions}</Box>
        ) : null}
      </Stack>
    </Box>
  );
};

export default WorkspacePageHeader;
